/**
 * features/tax-lookup/TaxIdLookupField.tsx — ช่องกรอกเลขผู้เสียภาษีพร้อมปุ่มค้นสรรพากร
 *
 * ใช้ร่วมกันในฟอร์มลูกค้า, ผู้ขาย และค่าใช้จ่าย
 * ถ้าเจอหลายสาขาจะเปิด dropdown ให้เลือก ระบบเดิมมีเคสนี้ (นิติบุคคลใหญ่มีหลายสาขา)
 */

import { useState } from 'react';
import { Search } from 'lucide-react';
import { formatBranch } from '@ft/core';
import { ApiError } from '~/lib/api';
import { Button, Field, Input } from '~/ui/primitives';
import { useToast } from '~/ui/overlays';
import type { TaxLookupBranch } from '~/lib/types';
import { useTaxLookup } from './hooks';

interface TaxIdLookupFieldProps {
  value: string;
  onChange: (value: string) => void;
  onFound: (branch: TaxLookupBranch) => void;
  error?: string;
}

export function TaxIdLookupField({ value, onChange, onFound, error }: TaxIdLookupFieldProps) {
  const lookup = useTaxLookup();
  const toast = useToast();
  const [branches, setBranches] = useState<TaxLookupBranch[] | null>(null);

  async function handleSearch() {
    setBranches(null);
    const digits = value.replace(/\D/g, '');
    if (digits.length !== 13) {
      toast.error('เลขผู้เสียภาษีต้องมี 13 หลัก');
      return;
    }

    try {
      const result = await lookup.mutateAsync(digits);
      if (result.branches.length === 1) {
        onFound(result.branches[0] as TaxLookupBranch);
        toast.success('พบข้อมูลและกรอกให้อัตโนมัติแล้ว');
      } else {
        setBranches(result.branches);
      }
    } catch (caught) {
      toast.error(caught instanceof ApiError ? caught.message : 'ค้นข้อมูลไม่สำเร็จ');
    }
  }

  return (
    <Field label="เลขประจำตัวผู้เสียภาษี" error={error}>
      <div className="flex gap-2">
        <Input
          value={value}
          onChange={(event) => onChange(event.target.value)}
          placeholder="13 หลัก"
          maxLength={13}
          invalid={Boolean(error)}
        />
        <Button onClick={() => void handleSearch()} loading={lookup.isPending} className="shrink-0">
          <Search size={15} />
          ค้นสรรพากร
        </Button>
      </div>

      {branches && branches.length > 0 ? (
        <div className="mt-2 space-y-1 rounded-md border border-border-base p-2">
          <p className="text-xs text-fg-muted">พบหลายสาขา กรุณาเลือก:</p>
          {branches.map((branch) => (
            <button
              key={branch.branch}
              type="button"
              onClick={() => {
                onFound(branch);
                setBranches(null);
              }}
              className="block w-full rounded px-2 py-1.5 text-left text-xs hover:bg-surface-hover"
            >
              <span className="font-medium">{formatBranch(branch.branch)}</span>
              <span className="ml-1.5 text-fg-muted">{branch.name}</span>
            </button>
          ))}
        </div>
      ) : null}
    </Field>
  );
}
